'use strict';

import PWABootstrap from '../pwa/PWABootstrap';
import { clearSessionResumeStorage } from '../utils/sessionResumeStorage';

/**
 * SessionResumeService handles automatic session restoration after page refresh
 * in kiosk mode. It detects if session data exists, checks service worker state,
 * and orchestrates the resume flow by broadcasting progress events (sessionResumeStarted,
 * sessionResumeCompleted, sessionResumeFailed) that consumers can use to display UI feedback.
 */
export class SessionResumeService {
    static $inject = [
        'PWAService',
        'AuthenticationBridgeClient',
        'AssessmentPackageManagerClient',
        '$rootScope',
        '$signals'
    ];

    constructor(PWAService, AuthenticationBridgeClient, AssessmentPackageManagerClient, $rootScope, $signals) {
        this.PWAService = PWAService;
        this.AuthenticationBridgeClient = AuthenticationBridgeClient;
        this.AssessmentPackageManagerClient = AssessmentPackageManagerClient;
        this.$rootScope = $rootScope;
        this.$signals = $signals;
        this.isResuming = false;
        this.resumeMessage = '';
    }

    /**
     * Safely extract error message from any error type
     * @param {*} err - Error object, string, or any value
     * @returns {string}
     */
    _getErrorMessage(err) {
        if (!err) return 'Unknown error';
        if (typeof err === 'string') return err;
        if (err.message) return err.message;
        if (typeof err.toString === 'function') {
            const str = err.toString();
            if (str !== '[object Object]') return str;
        }
        return 'Unknown error';
    }

    /**
     * Check if session resume is needed and possible
     * @returns {boolean}
     */
    needsSessionResume() {
        const hasResumeData = this.PWAService.hasSessionResumeData();
        console.log('SessionResumeService: needsSessionResume check - hasResumeData:', hasResumeData);
        return hasResumeData;
    }

    /**
     * Get the stored session resume data
     * @returns {{key: string|null, assessmentId: string|null, route: string|null}}
     */
    getResumeData() {
        return this.PWAService.getSessionResumeData();
    }

    /**
     * Attempt to resume the session
     * @returns {Promise<{success: boolean, route: string|null, error: string|null}>}
     */
    async attemptResume() {
        console.log('SessionResumeService: Starting session resume attempt');
        this.isResuming = true;
        this.resumeMessage = 'Checking session state...';
        this.$rootScope.$broadcast('sessionResumeStarted');

        try {
            const resumeData = this.getResumeData();
            console.log('SessionResumeService: Resume data:', resumeData);

            if (!resumeData.key || !resumeData.assessmentId) {
                console.log('SessionResumeService: Missing required resume data');
                this.isResuming = false;
                return { success: false, route: null, error: 'Missing session data' };
            }

            // Wait for service worker to be ready
            this.resumeMessage = 'Connecting to service worker...';
            await PWABootstrap.ready();

            // Check current service worker status
            this.resumeMessage = 'Checking service worker status...';
            const swStatus = await this.PWAService.checkServiceWorkerStatus();
            console.log('SessionResumeService: Service worker status:', swStatus);

            // If loader is not initialized, we need to reload the assessment
            if (!swStatus.loaderInitialized) {
                this.resumeMessage = 'Loading assessment...';
                console.log('SessionResumeService: Loader not initialized, loading assessment:', resumeData.assessmentId);

                try {
                    await this.AssessmentPackageManagerClient.loadAssessment(parseInt(resumeData.assessmentId, 10));
                    console.log('SessionResumeService: Assessment loaded successfully');
                } catch (loadError) {
                    console.error('SessionResumeService: Failed to load assessment:', loadError);
                    this.isResuming = false;
                    this.$rootScope.$broadcast('sessionResumeFailed', { error: 'Failed to load assessment' });
                    return { success: false, route: null, error: 'Failed to load assessment: ' + this._getErrorMessage(loadError) };
                }
            }

            // Always re-authenticate with stored key, even if service worker says it's authenticated
            // This is necessary because the frontend's AuthenticationBridgeService loses its state on page refresh
            // and needs the unlock response to restore the user data
            this.resumeMessage = 'Restoring authentication...';
            console.log('SessionResumeService: Attempting unlock with stored key to restore frontend auth state');

            const unlockResult = await this.attemptUnlock(resumeData.key);
            if (!unlockResult.success) {
                console.error('SessionResumeService: Failed to unlock:', unlockResult.error);
                this.isResuming = false;
                this.$rootScope.$broadcast('sessionResumeFailed', { error: 'Failed to authenticate' });
                return { success: false, route: null, error: 'Failed to authenticate: ' + unlockResult.error };
            }
            console.log('SessionResumeService: Unlock successful, frontend auth state restored');

            // Success! Determine where to navigate
            this.resumeMessage = 'Resuming session...';
            const targetRoute = resumeData.route || '/welcome';
            console.log('SessionResumeService: Resume successful, target route:', targetRoute);

            this.isResuming = false;
            this.$rootScope.$broadcast('sessionResumeCompleted', { route: targetRoute });

            return { success: true, route: targetRoute, error: null };

        } catch (error) {
            console.error('SessionResumeService: Resume failed with error:', error);
            this.isResuming = false;
            const errorMessage = this._getErrorMessage(error);
            this.$rootScope.$broadcast('sessionResumeFailed', { error: errorMessage });
            return { success: false, route: null, error: errorMessage };
        }
    }

    /**
     * Attempt to unlock using the stored key
     * @param {string} concatenatedKey
     * @returns {Promise<{success: boolean, error: string|null}>}
     */
    attemptUnlock(concatenatedKey) {
        return new Promise((resolve) => {
            console.log('SessionResumeService: Attempting unlock');

            let resolved = false;

            const timeout = setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    console.warn('SessionResumeService: Unlock timed out');
                    this.$signals.remove('tryUnlockSuccess', successHandler);
                    this.$signals.remove('tryUnlockFailed', failureHandler);
                    resolve({ success: false, error: 'Unlock timed out' });
                }
            }, 10000);

            // Listen for unlock result using $signals service
            const successHandler = () => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(timeout);
                    this.$signals.remove('tryUnlockSuccess', successHandler);
                    this.$signals.remove('tryUnlockFailed', failureHandler);
                    resolve({ success: true, error: null });
                }
            };

            const failureHandler = () => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(timeout);
                    this.$signals.remove('tryUnlockSuccess', successHandler);
                    this.$signals.remove('tryUnlockFailed', failureHandler);
                    resolve({ success: false, error: 'Invalid credentials' });
                }
            };

            // Subscribe to signals
            this.$signals.on('tryUnlockSuccess', successHandler);
            this.$signals.on('tryUnlockFailed', failureHandler);

            // Attempt the unlock
            this.AuthenticationBridgeClient.tryUnlock(concatenatedKey);
        });
    }

    /**
     * Clear session resume data. Called after explicit logout or failed resume attempts.
     * Uses AuthenticationBridgeClient.clearSessionResumeData() if available (PWA mode),
     * otherwise manually clears sessionStorage/localStorage keys.
     */
    clearResumeData() {
        console.log('SessionResumeService: Clearing resume data');
        if (this.AuthenticationBridgeClient.clearSessionResumeData) {
            this.AuthenticationBridgeClient.clearSessionResumeData();
        } else {
            console.warn('SessionResumeService: clearSessionResumeData not available on bridge, using fallback');
            clearSessionResumeStorage();
        }
    }
}
